import { AxiosError } from "axios";
import { useState } from "react";

import { PaymentScheduleOptions } from "@/components/Payments/AutoPayModal/SchedulePayModal";
import authAxiosInstance from "@/sevices/authAxiosInstance";
import { AutomaticPaymentType } from "@/types";
import { ErrorTypes } from "@/utils/Errors/errorMessages";
import { getErrorMessage } from "@/utils/Errors/getErrorMessage";

interface SchedulePaymentPayload {
  dueDate: string | undefined;
  invoiceId: string | undefined;
  paymentMethodId: string | undefined;
  scheduleOption: PaymentScheduleOptions | string;
  automaticPaymentType: AutomaticPaymentType;
  soldToOrgId: number | undefined;
}
interface UpdateSchedulePaymentPayload {
  dueDate: string | undefined;
  invoiceId: string | undefined;
  userId: string | undefined;
  paymentMethodId: string | undefined;
  scheduleOption?: PaymentScheduleOptions | string;
  automaticPaymentType: AutomaticPaymentType;
  specificDate?: string;
}

export const useSchedulePayment = () => {
  const [isFetchingSchedule, setIsFetchingSchedule] = useState(false);
  const [fetchScheduleError, setFetchScheduleError] = useState<string | null>(
    null
  );

  const createSchedulePayment = async (payload: SchedulePaymentPayload) => {
    try {
      const response = await authAxiosInstance({
        url: "paymentsservice/schedule/payment",
        method: "POST",
        data: {
          ...payload
        }
      });
      return response;
    } catch (err) {
      const error = err as AxiosError;
      throw error;
    }
  };

  const getScheduledPayment = async (invoiceId: string) => {
    setIsFetchingSchedule(true);

    try {
      const response = await authAxiosInstance.get(
        `paymentsservice/schedule/payment/invoice/${invoiceId}`
      );
      return response.data;
    } catch (err: unknown) {
      const error = err as AxiosError;
      if (error?.response?.status === 404) {
        // Silently ignore 404 — treat it as "no schedule exists"
        return null;
      }
      setFetchScheduleError(
        getErrorMessage(ErrorTypes.SchedulePaymentErrors, err)
      );
      return null;
    } finally {
      setIsFetchingSchedule(false);
    }
  };

  const updateScheduledPayment = async (
    schedulePaymentId: string,
    payload: UpdateSchedulePaymentPayload
  ) => {
    try {
      const response = await authAxiosInstance({
        url: `paymentsservice/schedule/payment/${schedulePaymentId}`,
        method: "POST",
        data: {
          ...payload
        }
      });
      return response;
    } catch (err: unknown) {
      const error = err as AxiosError;
      throw error;
    }
  };

  const cancelScheduledPayment = async (
    schedulePaymentId: string,
    emailAddresses: string[]
  ) => {
    try {
      const response = await authAxiosInstance.delete(
        `paymentsservice/schedule/payment/${schedulePaymentId}`,
        {
          data: { emailAddresses }
        }
      );
      return response;
    } catch (err: unknown) {
      const error = err as AxiosError;
      throw error;
    }
  };

  return {
    createSchedulePayment,
    getScheduledPayment,
    isFetchingSchedule,
    fetchScheduleError,
    setFetchScheduleError,
    updateScheduledPayment,
    cancelScheduledPayment
  };
};
